import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Dimmer, Loader, Popup, Input } from 'semantic-ui-react';
import DayPicker from 'react-day-picker';
import './calendar-design.less';
import moment from 'moment-timezone';
import UserContext from '../../../contexts/UserContext';
import { useFacility } from '../../contexts/facility/FacilityContext';
import usePolling from '../../hooks/usePolling';
import sendToast from '../../../utils/Toast';
import { fetchAllCalendarEvents, fetchAllResidentCalendarEvents } from '../../services/Calendar';
import { fetchAllAssets } from '../../services/Asset';
import { listDowMapping } from '../../services/Service';
import {
    ServiceCalendar,
    DalilyActivity,
    ResidentCalendar,
    SelectedCalendarEvent,
    CalendarTypeFilter,
    CalendarViewMode,
    DowColorMapping,
    CalendarDisplayEvent,
    RawCalendarEvent,
} from '../../types/Calendar';
import SelectedCalendarEventModal from '../../components/Calendar/SelectedEventModal';
import CreateCalendarEventModal from '../../components/Calendar/CreateCalendarEventModal';
import CalendarTimeGrid from '../../components/Calendar/CalendarTimeGrid';
import {
    MONTHS,
    WEEKDAYS,
    CalendarLayoutMode,
    addDays,
    buildWellnessFilterChips,
    getDateKey,
    eventMatchesWellnessFilter,
    eventMatchesViewAndSearch,
    getDotsForDate,
    getWeekStart,
    shiftMonth,
    toDisplayEvents,
    resolveCommentsEnabled,
    dowsFromColorMapping,
    sortDowsByMapping,
} from './calendarUtils';
import { customDateTimeFormat } from '../../../utils';
import { formatDateWithTZ } from '../../../utils/timezone';
import {
    CalendarSmallIcon,
    CardChevronIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EmptyDayIcon,
    PinIcon,
} from './CalendarIcons';

const LAYOUT_VIEWS: { id: CalendarLayoutMode; label: string }[] = [
    { id: 'month', label: 'Month' },
    { id: 'week', label: 'Week' },
    { id: 'day', label: 'Day' },
];

/* Layout-only Tailwind. Visuals (colors, DayPicker, FullCalendar) stay in calendar-design.less.
   `cal:` is 1100px — see tailwind.config.js. nbp-* tokens are px so they don't shrink with html font-size. */
const CAL_PAGE_SHELL =
    'neighbors-calendar-page box-border min-w-0 w-full overflow-x-hidden px-nbp-8 pb-nbp-24 pt-nbp-6 sm:px-nbp-12 md:px-nbp-16 lg:px-nbp-24 lg:pb-nbp-40';
const CAL_PAGE_GRID =
    'cal-page mx-auto grid min-w-0 w-full max-w-cal-page grid-cols-1 gap-nbp-24 px-0 pb-nbp-48 pt-nbp-8 cal:grid-cols-cal cal:gap-nbp-28 cal:px-nbp-12 cal:pb-nbp-64';
const CAL_PANEL = 'cal-panel flex min-w-0 w-full flex-col gap-nbp-20';
const CAL_TOOLBAR = 'cal-toolbar flex min-w-0 w-full flex-col items-start gap-nbp-10 sm:gap-nbp-12';
const CAL_VIEW_TOGGLE = 'cal-view-toggle flex w-full min-w-0 max-w-full gap-nbp-8 sm:w-auto';
const CAL_TOOLBAR_ROW = 'cal-toolbar-row flex min-w-0 w-full flex-wrap items-center gap-nbp-10';
const CAL_SCOPE_TOGGLE = 'cal-scope-toggle flex min-w-0 w-full flex-shrink-0 flex-wrap gap-nbp-8 sm:w-auto sm:flex-nowrap';
const CAL_SEARCH = 'cal-search-box min-w-0 w-full flex-auto basis-full sm:basis-[180px] sm:max-w-cal-search';
const CAL_TOOLBAR_ACTIONS = 'cal-toolbar-actions ml-auto flex flex-shrink-0 items-center gap-nbp-10';
const CAL_MONTH_NAV =
    'cal-month-nav min-w-0 w-full p-nbp-12 sm:p-nbp-16 md:p-nbp-20 lg:px-nbp-32 lg:pb-nbp-24 lg:pt-nbp-28';
const CAL_MONTH_ROW = 'cal-month-row mb-nbp-16 flex min-w-0 items-center justify-between gap-nbp-12 md:mb-nbp-28';
const CAL_MONTH_TITLE = 'cal-month-title min-w-0 break-words text-nbp-20 sm:text-nbp-24 lg:text-nbp-28';
const CAL_AGENDA = 'cal-agenda-panel flex min-w-0 w-full flex-col gap-nbp-20';
const CAL_FILTER_ROW = 'cal-filter-row flex min-w-0 w-full flex-wrap gap-nbp-8';
const CAL_EVENTS_LIST = 'cal-events-list flex min-w-0 w-full flex-col gap-nbp-12';

/** Four rows of three dots fill the day cell; anything past that clips. */
const MAX_VISIBLE_DOTS = 12;

/** Day cell body for react-day-picker v7 — day number + one dot per event. */
const CalendarDayContent = ({
    date,
    eventsByDate,
}: {
    date: Date;
    eventsByDate: Map<string, CalendarDisplayEvent[]>;
}) => {
    const dateKey = getDateKey(date.getFullYear(), date.getMonth(), date.getDate());
    const dots = getDotsForDate(eventsByDate, dateKey);
    const visibleDots = dots.slice(0, MAX_VISIBLE_DOTS);
    const overflow = dots.length - visibleDots.length;
    return (
        <>
            <div className={overflow > 0 ? 'cal-day-face has-more' : 'cal-day-face'}>
                <div className="cal-day-num">{date.getDate()}</div>
                {visibleDots.length > 0 && (
                    <div className="cal-dot-row">
                        {visibleDots.map((dot) => (
                            <div
                                key={dot.id}
                                className={`cal-dot${dot.color ? '' : ' uncategorized'}`}
                                style={dot.color ? { background: dot.color } : undefined}
                            />
                        ))}
                    </div>
                )}
                {overflow > 0 && (
                    <div className="cal-dot-more" aria-label={`${overflow} more events`}>+</div>
                )}
            </div>
        </>
    );
};

/** v7 replaces the whole classNames object — merge with library defaults. */
const dayPickerClassNames = {
    ...((DayPicker as any).defaultProps && (DayPicker as any).defaultProps.classNames),
    container: 'DayPicker cal-rdp',
    months: 'DayPicker-Months cal-rdp-months',
    month: 'DayPicker-Month cal-rdp-month',
    caption: 'cal-rdp-hidden',
    navBar: 'cal-rdp-hidden',
    weekdays: 'cal-rdp-hidden',
    weekdaysRow: 'cal-rdp-hidden',
    weekday: 'cal-rdp-hidden',
    body: 'DayPicker-Body cal-rdp-body',
    week: 'DayPicker-Week cal-rdp-week',
    day: 'DayPicker-Day cal-day',
    today: 'today',
    selected: 'selected',
    outside: 'other-month',
};

/** How often the calendar silently re-fetches so changes made elsewhere (e.g. by staff) appear. */
const CALENDAR_POLL_INTERVAL_MS = 60 * 1000;

const isServiceCalendar = (event: ServiceCalendar | DalilyActivity): event is ServiceCalendar =>
    (event as ServiceCalendar).svcActivityInstance !== undefined;

const Calendar = () => {
    const { facilityTimezone, loading: loadingFacilityInfo } = useFacility();
    const [selectedDateRange, setSelectedDateRange] = useState({
        startDateTimeString: '',
        endDateTimeString: '',
    });
    const [loadingCalendarEvents, setLoadingCalendarEvents] = useState<boolean>(true);
    const [residentCalendarEvents, setResidentCalendarEvents] = useState<ResidentCalendar[]>([]);
    const [normalCalendarEvents, setNormalCalendarEvents] = useState<DalilyActivity[]>([]);
    const [servicesCalendarEvents, setServicesCalendarEvents] = useState<ServiceCalendar[]>([]);
    const [openSelectedEventModal, setOpenSelectedEventModal] = useState<boolean>(false);
    const [selectedEventInfo, setSelectedEventInfo] = useState({} as SelectedCalendarEvent);
    const [openCreateEventModal, setOpenCreateEventModal] = useState<boolean>(false);
    const [isEditingResidentEvent, setIsEditingResidentEvent] = useState<boolean>(false);
    const [refreshCalendarEvents, setRefreshCalendarEvents] = useState<boolean>(false);
    const [activeFilter, setActiveFilter] = useState<CalendarTypeFilter>('all');
    const [layoutMode, setLayoutMode] = useState<CalendarLayoutMode>('month');
    const [viewMode, setViewMode] = useState<CalendarViewMode>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [assetNamesById, setAssetNamesById] = useState<Record<string, string>>({});
    const [dowColorMapping, setDowColorMapping] = useState<DowColorMapping>({});
    /** Snapshot of events for the selected day — kept across month fetches so the agenda doesn't clear. */
    const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarDisplayEvent[]>([]);
    /** Date range whose fetch last completed successfully (YYYY-MM-DD). Used so we don't wipe the agenda mid-navigate. */
    const [loadedDateRange, setLoadedDateRange] = useState({ startKey: '', endKey: '' });

    // Modifier class is toggled here rather than using :has() for older browsers —
    // same pattern as Menu-dining.tsx / resident-social's index.tsx.
    useLayoutEffect(() => {
        const shell = document.querySelector('.neighbors-interior-shell');
        if (!shell) return undefined;
        shell.classList.add('neighbors-interior-shell--calendar');
        return () => {
            shell.classList.remove('neighbors-interior-shell--calendar');
        };
    }, []);

    const today = useMemo((): Date | null => {
        if (!facilityTimezone) return null;
        const tz = moment.tz(facilityTimezone);
        return new Date(tz.year(), tz.month(), tz.date());
    }, [facilityTimezone]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());

    const {
        userObject: {
            registrants: { list: listOfResidents, activeRegistrantsIndexes },
        },
    } = useContext(UserContext);
    const correspondingSpeak2Resident = listOfResidents[activeRegistrantsIndexes[0]];

    // Anchor to facility "today" when timezone becomes available / changes — not when loading flips,
    // or navigating months would get reset if loadingFacilityInfo toggles.
    useEffect(() => {
        if (!facilityTimezone) return;
        const tz = moment.tz(facilityTimezone);
        const facilityToday = new Date(tz.year(), tz.month(), tz.date());
        setSelectedDate(facilityToday);
        setCurrentYear(facilityToday.getFullYear());
        setCurrentMonth(facilityToday.getMonth());
    }, [facilityTimezone]);

    useEffect(() => {
        if (!facilityTimezone && !loadingFacilityInfo) {
            setLoadingCalendarEvents(false);
        }
    }, [facilityTimezone, loadingFacilityInfo]);

    useEffect(() => {
        const fetchDowMapping = async () => {
            const facilityId = correspondingSpeak2Resident?.Facility;
            if (!facilityId) return;
            try {
                const colorMapping = await listDowMapping(facilityId);
                setDowColorMapping(colorMapping);
            } catch (error) {
                sendToast('error', error instanceof Error ? error.message : 'Failed to fetch DOW mapping');
            }
        };
        fetchDowMapping();
    }, [correspondingSpeak2Resident?.Facility]);

    // Sync fetch window to the visible month / week / day.
    useEffect(() => {
        if (!facilityTimezone || !selectedDate) return;

        let start: moment.Moment;
        let end: moment.Moment;

        if (layoutMode === 'month') {
            start = moment.tz({ year: currentYear, month: currentMonth, day: 1 }, facilityTimezone).startOf('month');
            end = start.clone().endOf('month');
        } else if (layoutMode === 'week') {
            const weekStart = getWeekStart(selectedDate);
            start = moment
                .tz(
                    { year: weekStart.getFullYear(), month: weekStart.getMonth(), day: weekStart.getDate() },
                    facilityTimezone,
                )
                .startOf('day');
            end = start.clone().add(6, 'days').endOf('day');
        } else {
            start = moment
                .tz(
                    {
                        year: selectedDate.getFullYear(),
                        month: selectedDate.getMonth(),
                        day: selectedDate.getDate(),
                    },
                    facilityTimezone,
                )
                .startOf('day');
            end = start.clone().endOf('day');
        }

        const startDate = start.format(customDateTimeFormat);
        const endDate = end.format(customDateTimeFormat);
        if (startDate === selectedDateRange.startDateTimeString && endDate === selectedDateRange.endDateTimeString) {
            return;
        }
        setSelectedDateRange({ startDateTimeString: startDate, endDateTimeString: endDate });
    }, [
        facilityTimezone,
        layoutMode,
        selectedDate,
        currentYear,
        currentMonth,
        selectedDateRange.startDateTimeString,
        selectedDateRange.endDateTimeString,
    ]);

    const dispatchMonthToHeader = useCallback((_year: number, month: number) => {
        window.dispatchEvent(new CustomEvent('calendarMonthChange', { detail: MONTHS[month] }));
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => dispatchMonthToHeader(currentYear, currentMonth), 100);
        return () => clearTimeout(timer);
    }, [currentYear, currentMonth, dispatchMonthToHeader]);

    // Always read the resident through a ref so the loader below doesn't change identity (and re-fire
    // the fetch effect) whenever the context hands back a new registrants array.
    const residentRef = useRef(correspondingSpeak2Resident);
    residentRef.current = correspondingSpeak2Resident;
    /**
     * Monotonic id of the newest calendar request. A response is applied only if its id is still the
     * newest, so a slow response can never overwrite fresher data (month changed, post-register
     * refresh, or a background poll racing a foreground load).
     */
    const latestLoadIdRef = useRef(0);
    /**
     * True while a foreground (loader-showing) request is in flight. Silent polls bail out while it is
     * set, so a poll can never take a newer id and make the foreground response stale — which would
     * leave the loader on forever.
     */
    const foregroundLoadInFlightRef = useRef(false);
    /** Asset id -> name already fetched. Names rarely change, so silent polls only fetch ids missing here. */
    const assetNameCacheRef = useRef<Record<string, string>>({});

    /**
     * Fetches events for `range` and applies them atomically.
     * `silent` is for background polling: no loader, no error toast (the next poll retries), and the
     * loading flag is left alone so the UI doesn't flicker.
     */
    const loadCalendarEvents = useCallback(
        async (range: { startDateTimeString: string; endDateTimeString: string }, { silent }: { silent: boolean }) => {
            const resident = residentRef.current;
            if (!range.startDateTimeString || !range.endDateTimeString || !facilityTimezone || !resident) return;
            if (silent && foregroundLoadInFlightRef.current) return;
            latestLoadIdRef.current += 1;
            const loadId = latestLoadIdRef.current;
            const isStale = () => loadId !== latestLoadIdRef.current;
            if (!silent) foregroundLoadInFlightRef.current = true;
            try {
                if (!silent) setLoadingCalendarEvents(true);
                const facilityId = resident.Facility;
                const startDateTime = moment.tz(range.startDateTimeString, facilityTimezone).format(customDateTimeFormat);
                const endDateTime = moment.tz(range.endDateTimeString, facilityTimezone).format(customDateTimeFormat);
                const calendarResponse = await fetchAllCalendarEvents({
                    facilityId,
                    Filter: {
                        startDateTimeString: startDateTime,
                        endDateTimeString: endDateTime,
                        ...(resident.Unit?.RoomCategory && {
                            calendarType: [resident.Unit.RoomCategory.toString()],
                        }),
                    },
                    ResidentIds: [resident._id],
                    includeDowMapping: true,
                });
                if (isStale()) return;

                const serviceCalendarEvents: ServiceCalendar[] = [];
                const normalEvents: DalilyActivity[] = [];
                calendarResponse.forEach((event: ServiceCalendar | DalilyActivity) => {
                    if (isServiceCalendar(event)) {
                        serviceCalendarEvents.push(event);
                    } else {
                        normalEvents.push(event);
                    }
                });

                const assetIds = serviceCalendarEvents
                    .map((event) => event.Asset)
                    .filter(Boolean);

                const getAssetNamesById = async (): Promise<Record<string, string>> => {
                    if (assetIds.length === 0) return {};
                    // Foreground loads refresh every name; silent polls only fetch ids not cached yet.
                    const idsToFetch = silent
                        ? Array.from(new Set(assetIds)).filter((id) => !assetNameCacheRef.current[id])
                        : assetIds;
                    try {
                        if (idsToFetch.length > 0) {
                            const assets = await fetchAllAssets(facilityId, idsToFetch);
                            assets.forEach((asset) => {
                                if (asset?._id && asset?.AssetName) {
                                    assetNameCacheRef.current[String(asset._id)] = String(asset.AssetName);
                                }
                            });
                        }
                    } catch (assetError) {
                        if (!silent) {
                            sendToast('error', assetError instanceof Error ? assetError.message : 'Failed to fetch asset names');
                        }
                    }
                    const nextAssetNames: Record<string, string> = {};
                    assetIds.forEach((id) => {
                        if (assetNameCacheRef.current[id]) nextAssetNames[id] = assetNameCacheRef.current[id];
                    });
                    return nextAssetNames;
                };

                const [nextAssetNames, residentCalendarResponse] = await Promise.all([
                    getAssetNamesById(),
                    fetchAllResidentCalendarEvents({
                        facilityId,
                        Filter: {
                            registrantId: resident._id,
                            startDate: startDateTime,
                            endDate: endDateTime,
                        },
                    }),
                ]);
                if (isStale()) return;

                // React 16 doesn't batch async setStates — apply the month payload atomically so
                // the agenda snapshot can't sync against empty/partial buffers mid-update.
                unstable_batchedUpdates(() => {
                    setAssetNamesById(nextAssetNames);
                    setServicesCalendarEvents(serviceCalendarEvents);
                    setNormalCalendarEvents(normalEvents);
                    setResidentCalendarEvents(residentCalendarResponse.Result);
                    setLoadedDateRange({
                        startKey: range.startDateTimeString.slice(0, 10),
                        endKey: range.endDateTimeString.slice(0, 10),
                    });
                    if (!silent) setLoadingCalendarEvents(false);
                });
                if (!silent) foregroundLoadInFlightRef.current = false;
            } catch (error) {
                if (isStale()) return;
                if (silent) return;
                foregroundLoadInFlightRef.current = false;
                sendToast('error', error instanceof Error ? error.message : 'Failed to fetch Calendar Events');
                setLoadingCalendarEvents(false);
            }
        },
        [facilityTimezone],
    );

    // Foreground load: runs on range change, timezone change, and after the user registers/edits
    // (refreshCalendarEvents). Shows the loader and reports errors.
    useEffect(() => {
        loadCalendarEvents(
            { startDateTimeString: selectedDateRange.startDateTimeString, endDateTimeString: selectedDateRange.endDateTimeString },
            { silent: false },
        );
        return () => {
            // Invalidate any in-flight request so a late response can't touch unmounted/changed state.
            latestLoadIdRef.current += 1;
            foregroundLoadInFlightRef.current = false;
        };
    }, [loadCalendarEvents, selectedDateRange.startDateTimeString, selectedDateRange.endDateTimeString, refreshCalendarEvents]);

    // Background refresh so changes made elsewhere (e.g. staff moving a resident out of Registered)
    // show up without a month change or page reload. Skipped while a foreground load is running and
    // while a modal is open, so it can't race the modal's own post-save refresh.
    usePolling(
        () => {
            if (loadingCalendarEvents || openSelectedEventModal || openCreateEventModal) return undefined;
            return loadCalendarEvents(selectedDateRange, { silent: true });
        },
        { intervalMs: CALENDAR_POLL_INTERVAL_MS, enabled: !!facilityTimezone },
    );

    const getAttendingStatus = (residentAttendees?: { id: string; status: string }[]) => {
        if (!residentAttendees || !residentAttendees[0]) return 'notattending';
        return residentAttendees[0].status;
    };

    const combinedEvents = useMemo((): RawCalendarEvent[] => {
        if (!facilityTimezone) return [];
        const sourceEventIds = new Set<string>();

        const serviceEvents: RawCalendarEvent[] = servicesCalendarEvents.map((event) => {
            const status = getAttendingStatus(event.residentAttendees);
            const externalCost = event.defaults?.externalCost ? parseFloat(event.defaults.externalCost) : 0;
            const sourceEventId = String(event.srcId || '');
            const instanceEventId = String(event._id || '');
            if (sourceEventId) sourceEventIds.add(sourceEventId);
            if (instanceEventId) sourceEventIds.add(instanceEventId);
            return {
                title: event.name,
                start: formatDateWithTZ(event.startDate, customDateTimeFormat, facilityTimezone),
                end: formatDateWithTZ(event.endDate, customDateTimeFormat, facilityTimezone),
                location: (event.Asset && assetNamesById[event.Asset]) || undefined,
                description: event.shortDescription || null,
                longDescription: event.longDescription || null,
                image: event.image || null,
                attendingStatus: event.residentAttendees ? event.residentAttendees[0] : 'notattending',
                typeOfRequest: 'service',
                svcInstanceId: event.srcId ? event.srcId : event._id,
                attendingId: event._id,
                showAttendance: event.showAttendance || false,
                isRegistrationClosed: event.isRegistrationClosed || false,
                docIds: event.DocIds || [],
                externalCost: externalCost > 0 ? `$${externalCost.toFixed(2)}` : null,
                estimatedCharge: event.estimatedCharge || null,
                registrationStatus: status,
                commentsEnabled: resolveCommentsEnabled(event),
                dominantDow: event.dowColorMapping?.dominantDow ?? null,
                dominantColor: event.dowColorMapping?.dominantColor ?? null,
                dows: dowsFromColorMapping(
                    event.dowColorMapping?.colorMapping,
                    event.dowColorMapping?.dominantDow ?? null,
                    event.dowColorMapping?.dominantColor ?? null,
                ),
            };
        });

        const normalEvents: RawCalendarEvent[] = normalCalendarEvents.map((event) => {
            const status = getAttendingStatus(event.residentAttendees);
            const sourceEventId = String(event.srcId || '');
            const instanceEventId = String(event._id || '');
            if (sourceEventId) sourceEventIds.add(sourceEventId);
            if (instanceEventId) sourceEventIds.add(instanceEventId);
            return {
                title: event.text,
                start: formatDateWithTZ(event.dateTimeString, customDateTimeFormat, facilityTimezone),
                location: undefined,
                description: null,
                longDescription: null,
                image: null,
                attendingStatus: event.residentAttendees ? event.residentAttendees[0] : 'notattending',
                typeOfRequest: 'normal',
                contentId: event.srcId ? event.srcId : event._id,
                attendingId: event._id,
                showAttendance: event.showAttendance || false,
                externalCost: null,
                registrationStatus: status,
                dominantDow: event.dowColorMapping?.dominantDow || null,
                dominantColor: event.dowColorMapping?.dominantColor || null,
                dows: dowsFromColorMapping(
                    event.dowColorMapping?.colorMapping,
                    event.dowColorMapping?.dominantDow || null,
                    event.dowColorMapping?.dominantColor || null,
                ),
            };
        });

        const residentEvents: RawCalendarEvent[] = residentCalendarEvents.reduce<RawCalendarEvent[]>(
            (events, event) => {
                const residentInstanceId = String(event.instanceId || '');
                if (residentInstanceId && sourceEventIds.has(residentInstanceId)) return events;
                const isWaiting = event.text?.toLowerCase().includes('waiting list');
                events.push({
                    title: event.text,
                    start: formatDateWithTZ(event.dateTimeString, customDateTimeFormat, facilityTimezone),
                    end: formatDateWithTZ(event.endDateTimeString, customDateTimeFormat, facilityTimezone),
                    location: event.location,
                    description: null,
                    longDescription: null,
                    image: null,
                    attendingStatus: isWaiting ? 'Waiting List' : 'Attended',
                    typeOfRequest: 'resident',
                    attendingId: event._id,
                    showAttendance: event.showAttendance ?? true,
                    externalCost: null,
                    registrationStatus: isWaiting ? 'waiting' : 'attended',
                    registrantIds: Array.isArray(event.registrantIds) ? event.registrantIds.map(String) : [],
                    dominantDow: null,
                    dominantColor: null,
                    dows: [],
                });
                return events;
            },
            [],
        );

        return [...serviceEvents, ...normalEvents, ...residentEvents];
    }, [servicesCalendarEvents, normalCalendarEvents, residentCalendarEvents, facilityTimezone, assetNamesById]);

    const displayEvents = useMemo(() => {
        if (!facilityTimezone) return [];
        return toDisplayEvents(combinedEvents, facilityTimezone, (event, start) => {
            const normalizedDocIds = Array.from(
                new Set<string>((event.docIds || []).filter(Boolean).map(String)),
            );
            const endMoment = event.end ? moment.tz(event.end, facilityTimezone) : null;
            const durationMinutes = endMoment && endMoment.isValid()
                ? endMoment.diff(start, 'minutes')
                : undefined;
            return {
                title: event.title,
                startTime: start.format('hh:mm A'),
                date: start.format('M/D/YYYY'),
                endTime: endMoment && endMoment.isValid() ? endMoment.format('h:mm A') : undefined,
                endDateTimeString: event.end || undefined,
                durationMinutes: durationMinutes && durationMinutes > 0 ? durationMinutes : undefined,
                location: event.location || '',
                description: event.description ?? null,
                longDescription: event.longDescription ?? null,
                image: event.image || '',
                attendingStatus: event.attendingStatus as SelectedCalendarEvent['attendingStatus'],
                svcInstanceId: event.svcInstanceId ?? null,
                typeOfRequest: event.typeOfRequest,
                contentId: event.contentId ?? null,
                Facility: correspondingSpeak2Resident.Facility,
                registrantId: correspondingSpeak2Resident._id,
                attendingId: event.attendingId || '',
                showAttendance: event.showAttendance,
                isRegistrationClosed: event.isRegistrationClosed,
                registrant: {
                    FirstName: correspondingSpeak2Resident.FirstName,
                    LastName: correspondingSpeak2Resident.LastName,
                },
                docIds: normalizedDocIds,
                externalCost: event.externalCost || undefined,
                estimatedCharge: event.estimatedCharge || undefined,
                commentsEnabled: event.commentsEnabled ?? false,
                registrantIds: event.registrantIds,
            };
        });
    }, [combinedEvents, facilityTimezone, correspondingSpeak2Resident]);

    const filteredDisplayEvents = useMemo(() => {
        return displayEvents.filter((event) =>
            eventMatchesViewAndSearch(event, viewMode, searchQuery),
        );
    }, [displayEvents, viewMode, searchQuery]);

    /** Unfiltered day buckets — used to cache the selected day's full event list. */
    const eventsByDateUnfiltered = useMemo(() => {
        const map = new Map<string, CalendarDisplayEvent[]>();
        displayEvents.forEach((event) => {
            if (!map.has(event.dateKey)) {
                map.set(event.dateKey, []);
            }
            map.get(event.dateKey)!.push(event);
        });
        return map;
    }, [displayEvents]);

    /** Filtered day buckets — drives month dots and time-grid (scope/search applied). */
    const eventsByDate = useMemo(() => {
        const map = new Map<string, CalendarDisplayEvent[]>();
        filteredDisplayEvents.forEach((event) => {
            if (!map.has(event.dateKey)) {
                map.set(event.dateKey, []);
            }
            map.get(event.dateKey)!.push(event);
        });
        return map;
    }, [filteredDisplayEvents]);

    /** Time-grid events filtered by the active wellness chip. */
    const filteredEventsByDate = useMemo(() => {
        if (activeFilter === 'all') return eventsByDate;
        const map = new Map<string, CalendarDisplayEvent[]>();
        eventsByDate.forEach((events, key) => {
            const filtered = events.filter((event) =>
                eventMatchesWellnessFilter(event.dows, activeFilter),
            );
            if (filtered.length > 0) map.set(key, filtered);
        });
        return map;
    }, [eventsByDate, activeFilter]);

    const todayKey = useMemo(() => 
        today ? getDateKey(today.getFullYear(), today.getMonth(), today.getDate()) : '',
    [today]);

    const selectedKey = useMemo(() => 
        selectedDate ? getDateKey(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) : '',
    [selectedDate]);
    const prevSelectedKeyRef = useRef(selectedKey);

    // Keep a selected-day agenda snapshot across month navigation (full day, unfiltered).
    // Scope / search / wellness filters are applied when rendering the agenda.
    // Only clear it when the user picks a new day that isn't in the loaded range (ghost / Today).
    useEffect(() => {
        const selectedKeyChanged = prevSelectedKeyRef.current !== selectedKey;
        prevSelectedKeyRef.current = selectedKey;

        if (!selectedKey || !selectedDate) {
            setSelectedDayEvents([]);
            return;
        }
        const { startKey, endKey } = loadedDateRange;
        const inLoadedRange = !!(startKey && endKey && selectedKey >= startKey && selectedKey <= endKey);

        if (!inLoadedRange) {
            if (selectedKeyChanged) setSelectedDayEvents([]);
            return;
        }
        if (loadingCalendarEvents) return;
        setSelectedDayEvents(eventsByDateUnfiltered.get(selectedKey) || []);
    }, [selectedKey, selectedDate, eventsByDateUnfiltered, loadedDateRange, loadingCalendarEvents]);

    const agendaEvents = useMemo(() => {
        return selectedDayEvents.filter((event) =>
            eventMatchesViewAndSearch(event, viewMode, searchQuery)
            && eventMatchesWellnessFilter(event.dows, activeFilter),
        );
    }, [selectedDayEvents, viewMode, searchQuery, activeFilter]);

    const pendingRangeStartKey = selectedDateRange.startDateTimeString.slice(0, 10);
    const pendingRangeEndKey = selectedDateRange.endDateTimeString.slice(0, 10);
    const selectedDayInPendingRange =
        !!selectedKey &&
        !!pendingRangeStartKey &&
        selectedKey >= pendingRangeStartKey &&
        selectedKey <= pendingRangeEndKey;
    const selectedDayOutsideLoaded =
        !!selectedKey &&
        (!loadedDateRange.startKey ||
            selectedKey < loadedDateRange.startKey ||
            selectedKey > loadedDateRange.endKey);
    const isSelectedDayPending = loadingCalendarEvents && selectedDayInPendingRange;
    // Selected day has no cached agenda yet, and we're fetching a range that includes it
    // (e.g. Today / ghost day). Require pending-range membership so month-arrow nav away
    // from the selected day can't get stuck in a loader-only state.
    const selectedDayMissingFromCache =
        selectedDayOutsideLoaded && selectedDayEvents.length === 0 && selectedDayInPendingRange;
    const isRefreshingSelectedDay = isSelectedDayPending || selectedDayMissingFromCache;
    const agendaLoadingOnly = isRefreshingSelectedDay;

    const visibleMonth = useMemo(
        () => new Date(currentYear, currentMonth, 1),
        [currentYear, currentMonth],
    );

    const wellnessFilterChips = useMemo(
        () => buildWellnessFilterChips(dowColorMapping),
        [dowColorMapping],
    );

    const syncMonthFromDate = (date: Date) => {
        if (date.getMonth() !== currentMonth || date.getFullYear() !== currentYear) {
            setCurrentYear(date.getFullYear());
            setCurrentMonth(date.getMonth());
            dispatchMonthToHeader(date.getFullYear(), date.getMonth());
        }
    };

    const changeMonth = (dir: -1 | 1) => {
        const { year, month } = shiftMonth(currentYear, currentMonth, dir);
        setCurrentMonth(month);
        setCurrentYear(year);
    };

    const handleMonthChange = (monthDate: Date) => {
        setCurrentYear(monthDate.getFullYear());
        setCurrentMonth(monthDate.getMonth());
    };

    const changeWeek = (dir: -1 | 1) => {
        if (!selectedDate) return;
        const next = addDays(selectedDate, dir * 7);
        setSelectedDate(next);
        syncMonthFromDate(next);
    };

    const changeDay = (dir: -1 | 1) => {
        if (!selectedDate) return;
        const next = addDays(selectedDate, dir);
        setSelectedDate(next);
        syncMonthFromDate(next);
    };

    const handleToday = () => {
        if (!today) return;
        setCurrentYear(today.getFullYear());
        setCurrentMonth(today.getMonth());
        setSelectedDate(today);
    };

    const selectDay = (year: number, month: number, day: number) => {
        setSelectedDate(new Date(year, month, day));
        if (month !== currentMonth || year !== currentYear) {
            setCurrentYear(year);
            setCurrentMonth(month);
        }
    };

    /** Shared day selection for month DayPicker and week/day grids. */
    const handleDayClick = (date: Date) => {
        selectDay(date.getFullYear(), date.getMonth(), date.getDate());
    };

    const openEvent = (evt: CalendarDisplayEvent) => {
        setSelectedEventInfo(evt.selectedEventInfo);
        setOpenSelectedEventModal(true);
    };

    const closeSelectedEventModal = () => setOpenSelectedEventModal(false);

    const closeCreateEventModal = () => {
        setSelectedEventInfo({} as SelectedCalendarEvent);
        setIsEditingResidentEvent(false);
        setOpenCreateEventModal(false);
    };

    const openEditResidentEvent = () => {
        setIsEditingResidentEvent(true);
        setOpenSelectedEventModal(false);
        setOpenCreateEventModal(true);
    };

    const openCreateResidentEvent = () => {
        setIsEditingResidentEvent(false);
        setOpenCreateEventModal(true);
    };

    const createEventInfo = {
        registrantIds: [correspondingSpeak2Resident._id],
        facilityId: correspondingSpeak2Resident.Facility,
    };

    const eventCountLabel =
        agendaEvents.length === 0
            ? 'No events'
            : agendaEvents.length === 1
              ? '1 event scheduled'
              : `${agendaEvents.length} events scheduled`;

    return (
        <div className={CAL_PAGE_SHELL}>
            <div className={CAL_PAGE_GRID}>
                <div className={CAL_PANEL}>
                    <div className={CAL_TOOLBAR}>
                        <div className={CAL_VIEW_TOGGLE}>
                            {LAYOUT_VIEWS.map((view) => (
                                <button
                                    key={view.id}
                                    type="button"
                                    className={`cal-view-btn flex-1 text-center sm:flex-none${layoutMode === view.id ? ' active' : ''}`}
                                    aria-pressed={layoutMode === view.id}
                                    onClick={() => setLayoutMode(view.id)}
                                >
                                    {view.label}
                                </button>
                            ))}
                        </div>
                        <div className={CAL_TOOLBAR_ROW}>
                            <div className={CAL_SCOPE_TOGGLE}>
                                <button
                                    type="button"
                                    className={`cal-scope-btn flex-1 sm:flex-none${viewMode === 'all' ? ' active' : ''}`}
                                    onClick={() => setViewMode('all')}
                                >
                                    View all events
                                </button>
                                <button
                                    type="button"
                                    className={`cal-scope-btn registrations flex-1 sm:flex-none${viewMode === 'registrations' ? ' active' : ''}`}
                                    onClick={() => setViewMode('registrations')}
                                >
                                    Your registrations
                                </button>
                            </div>
                            <div className={CAL_SEARCH}>
                                <Input
                                    icon="search"
                                    placeholder="Search events..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                />
                            </div>
                            <div className={CAL_TOOLBAR_ACTIONS}>
                                <button
                                    type="button"
                                    className="cal-create-btn h-nbp-40 w-nbp-40 text-nbp-20 sm:h-nbp-44 sm:w-nbp-44"
                                    onClick={openCreateResidentEvent}
                                    aria-label="Create event"
                                    title="Create event"
                                >
                                    +
                                </button>
                                <button type="button" className="cal-today-btn" onClick={handleToday}>
                                    Today
                                </button>
                            </div>
                        </div>
                    </div>

                    {layoutMode === 'month' && (
                        <div className={CAL_MONTH_NAV}>
                            <div className={CAL_MONTH_ROW}>
                                <div className={CAL_MONTH_TITLE}>
                                    {MONTHS[currentMonth]} <span>{currentYear}</span>
                                </div>
                                <div className="cal-nav-arrows flex flex-shrink-0 gap-nbp-10">
                                    <button
                                        type="button"
                                        className="cal-nav-btn h-nbp-36 w-nbp-36 sm:h-nbp-40 sm:w-nbp-40 md:h-nbp-44 md:w-nbp-44"
                                        onClick={() => changeMonth(-1)}
                                        aria-label="Previous month"
                                    >
                                        <ChevronLeftIcon />
                                    </button>
                                    <button
                                        type="button"
                                        className="cal-nav-btn h-nbp-36 w-nbp-36 sm:h-nbp-40 sm:w-nbp-40 md:h-nbp-44 md:w-nbp-44"
                                        onClick={() => changeMonth(1)}
                                        aria-label="Next month"
                                    >
                                        <ChevronRightIcon />
                                    </button>
                                </div>
                            </div>

                            <div className="cal-day-headers">
                                {WEEKDAYS.map((name, index) => (
                                    <div
                                        key={name}
                                        className={`cal-day-header${index === 0 || index === 6 ? ' weekend' : ''}`}
                                    >
                                        <span className="cal-day-header-full hidden sm:inline">{name.slice(0, 3)}</span>
                                        <span className="cal-day-header-short inline sm:hidden">{name.slice(0, 2)}</span>
                                    </div>
                                ))}
                            </div>

                            <Dimmer.Dimmable
                                as="div"
                                className="cal-grid-wrap"
                                dimmed={loadingFacilityInfo || loadingCalendarEvents}
                            >
                                <Dimmer active={loadingFacilityInfo || loadingCalendarEvents} inverted>
                                    <Loader content="Loading Calendar Events" />
                                </Dimmer>
                                <DayPicker
                                    month={visibleMonth}
                                    onMonthChange={handleMonthChange}
                                    selectedDays={selectedDate || undefined}
                                    onDayClick={handleDayClick}
                                    modifiers={today ? { today } : undefined}
                                    showOutsideDays
                                    enableOutsideDaysClick
                                    firstDayOfWeek={0}
                                    canChangeMonth={false}
                                    classNames={dayPickerClassNames}
                                    renderDay={(date) => (
                                        <CalendarDayContent
                                            date={date}
                                            eventsByDate={eventsByDate}
                                        />
                                    )}
                                />
                            </Dimmer.Dimmable>
                        </div>
                    )}

                    {(layoutMode === 'week' || layoutMode === 'day') && selectedDate && (
                        <Dimmer.Dimmable
                            as="div"
                            className="cal-time-dimmable min-w-0 w-full max-w-full"
                            dimmed={loadingFacilityInfo || loadingCalendarEvents}
                        >
                            <Dimmer active={loadingFacilityInfo || loadingCalendarEvents} inverted>
                                <Loader content="Loading Calendar Events" />
                            </Dimmer>
                            <CalendarTimeGrid
                                mode={layoutMode}
                                selectedDate={selectedDate}
                                todayKey={todayKey}
                                selectedKey={selectedKey}
                                facilityTimezone={facilityTimezone}
                                eventsByDate={filteredEventsByDate}
                                onSelectDate={handleDayClick}
                                onOpenEvent={openEvent}
                                onPrev={() => (layoutMode === 'week' ? changeWeek(-1) : changeDay(-1))}
                                onNext={() => (layoutMode === 'week' ? changeWeek(1) : changeDay(1))}
                            />
                        </Dimmer.Dimmable>
                    )}
                </div>

                <div className={CAL_AGENDA}>
                    <div className="cal-agenda-header p-nbp-16 sm:p-nbp-20 md:p-nbp-28">
                        <div className="cal-agenda-day-label">Selected Day</div>
                        <div className="cal-agenda-day-name min-w-0 break-words text-nbp-20 sm:text-nbp-24">
                            {selectedDate != null
                                ? `${WEEKDAYS[selectedDate.getDay()]}, ${MONTHS[selectedDate.getMonth()]} ${selectedDate.getDate()}`
                                : '\u00a0'}
                        </div>
                        <div className="cal-agenda-date-sub">
                            {selectedKey === todayKey ? 'Today' : '\u00a0'}
                        </div>
                        <div className="cal-event-count-pill">
                            <CalendarSmallIcon />
                            {!agendaLoadingOnly && eventCountLabel}
                            {isRefreshingSelectedDay && (
                                <Loader active inline inverted size="mini" className="cal-agenda-inline-loader" />
                            )}
                        </div>
                    </div>

                    <div className={CAL_FILTER_ROW}>
                        {wellnessFilterChips.map((chip) => {
                            const chipStyle = 'color' in chip
                                ? { '--chip-color': chip.color } as React.CSSProperties
                                : undefined;
                            return (
                                <button
                                    key={chip.id}
                                    type="button"
                                    className={`cal-chip ${chip.id === 'all' ? 'all' : 'wellness'}${activeFilter === chip.id ? ' active' : ''}`}
                                    style={chipStyle}
                                    onClick={() => setActiveFilter(chip.id)}
                                >
                                    {'color' in chip && (
                                        <span className="cal-chip-dot" style={{ background: chip.color }} />
                                    )}
                                    {chip.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className={CAL_EVENTS_LIST}>
                        {agendaLoadingOnly ? null : agendaEvents.length === 0 ? (
                            <div className="cal-empty-state">
                                <div className="cal-empty-icon">
                                    <EmptyDayIcon />
                                </div>
                                <div className="cal-empty-text">
                                    No events on this day.
                                    <br />
                                    Enjoy some quiet time!
                                </div>
                            </div>
                        ) : (
                            agendaEvents.map((event, index) => {
                                const hasDow = event.dows.length > 0 && !!event.dominantColor;
                                const cardStyle: React.CSSProperties = {
                                    animationDelay: `${0.05 + index * 0.06}s`,
                                    ...(hasDow ? { '--dow-color': event.dominantColor } as React.CSSProperties : {}),
                                };
                                return (
                                    <div
                                        key={event.id}
                                        className={`cal-event-card flex min-w-0 w-full max-w-full items-center${hasDow ? ' wellness' : ' uncategorized'}`}
                                        style={cardStyle}
                                        onClick={() => openEvent(event)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openEvent(event);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="cal-event-time-col">
                                            <div className="cal-event-time">{event.time}</div>
                                            <div className="cal-event-ampm">{event.ampm}</div>
                                            {event.isWaitlisted && <div className="cal-rsvp-badge cal-rsvp-badge--waiting">Waiting</div>}
                                            {event.isRegistrationClosed && <div className="cal-rsvp-badge cal-rsvp-badge--closed">Closed</div>}
                                            {event.isDeclined && <div className="cal-rsvp-badge cal-rsvp-badge--declined">Declined</div>}
                                            {event.rsvp && !event.isWaitlisted && <div className="cal-rsvp-badge">Registered</div>}
                                            {event.canRegister && (
                                                <button
                                                    type="button"
                                                    className="cal-register-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEvent(event);
                                                    }}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                >
                                                    Register
                                                </button>
                                            )}
                                        </div>
                                        <div className="cal-event-divider" />
                                        <div className="cal-event-info min-w-0 flex-1">
                                            <Popup
                                                trigger={<div className="cal-event-title min-w-0 truncate">{event.title}</div>}
                                                content={event.title}
                                                position="top left"
                                                size="tiny"
                                            />
                                            <div className="cal-event-meta">
                                                {event.dows.length > 0 && (
                                                    <div className="cal-event-tags">
                                                        {sortDowsByMapping(event.dows, dowColorMapping).map((dow) => (
                                                            <span
                                                                key={dow.id}
                                                                className="cal-event-tag wellness"
                                                                style={{ '--dow-color': dow.color } as React.CSSProperties}
                                                            >
                                                                {dow.id}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                {event.location ? (
                                                    <div className="cal-event-location">
                                                        <PinIcon />
                                                        <Popup
                                                            trigger={<span className="cal-event-location-text">{event.location}</span>}
                                                            content={event.location}
                                                            position="top center"
                                                            size="tiny"
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="cal-event-action">
                                            <CardChevronIcon />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        {isRefreshingSelectedDay && (
                            <div className="cal-agenda-list-loader">
                                <Loader active inline size="small" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <SelectedCalendarEventModal
                closeSelectedEventModal={closeSelectedEventModal}
                openSelectedEventModal={openSelectedEventModal}
                selectedEventInfo={selectedEventInfo}
                refreshCalendarEvents={refreshCalendarEvents}
                setRefreshCalendarEvents={setRefreshCalendarEvents}
                facilityTimezone={facilityTimezone}
                onEditResidentEvent={openEditResidentEvent}
            />

            <CreateCalendarEventModal
                closeCreateEventModal={closeCreateEventModal}
                openCreateCalendarModal={openCreateEventModal}
                createEventInfo={createEventInfo}
                refreshCalendarEvents={refreshCalendarEvents}
                setRefreshCalendarEvents={setRefreshCalendarEvents}
                isEditingResidentEvent={isEditingResidentEvent}
                eventToEdit={isEditingResidentEvent ? selectedEventInfo : undefined}
            />
        </div>
    );
};

export default Calendar;
